import { useEffect, useRef, useState } from 'react';
import { placesApi, type AutocompletePrediction } from '../../api/places.api';
import { Icon, MapPin } from '../ui/Icon';

interface Props {
  value:        string;
  onChange:     (value: string) => void;
  placeholder?: string;
}

function genToken(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function DestinationAutosuggest({ value, onChange, placeholder }: Props) {
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const [open, setOpen]               = useState(false);
  const [highlight, setHighlight]     = useState(0);
  const sessionTokenRef = useRef<string>(genToken());
  const wrapRef         = useRef<HTMLDivElement>(null);
  const isFocused       = useRef(false);
  const skipNextFetch   = useRef(false);

  useEffect(() => {
    if (skipNextFetch.current) { skipNextFetch.current = false; return; }
    if (!isFocused.current) return;
    const q = value.trim();
    if (q.length < 2) {
      setPredictions([]); setOpen(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const list = await placesApi.autocomplete(q, sessionTokenRef.current);
        setPredictions(list);
        setOpen(list.length > 0);
        setHighlight(0);
      } catch {
        setPredictions([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function handleSelect(p: AutocompletePrediction) {
    skipNextFetch.current = true;
    onChange(p.mainText);
    setOpen(false);
    setPredictions([]);
    sessionTokenRef.current = genToken();
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || predictions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => (h + 1) % predictions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => (h - 1 + predictions.length) % predictions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(predictions[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => { isFocused.current = true; if (predictions.length > 0) setOpen(true); }}
        onBlur={() => { isFocused.current = false; }}
        onKeyDown={handleKey}
        placeholder={placeholder}
        className="w-full h-10 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 outline-none focus:border-teal-400 transition-colors"
      />
      {open && predictions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden max-h-72 overflow-y-auto">
          {predictions.map((p, i) => (
            <button
              key={p.placeId}
              type="button"
              onMouseDown={e => { e.preventDefault(); handleSelect(p); }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full flex items-start gap-2 px-3 py-2 text-left transition-colors ${
                i === highlight
                  ? 'bg-teal-50 dark:bg-teal-900/20'
                  : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
              }`}
            >
              <Icon as={MapPin} size={14} className="text-slate-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-slate-800 dark:text-slate-200 truncate">{p.mainText}</p>
                {p.secondaryText && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{p.secondaryText}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
